import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useRouteParams } from "./useRouteParams";
import {
  onlyChunkParamHasChanged,
  sanitiseQueryParams,
} from "@/utils/queryUtils";

export default function useRouteLoading() {
  const router = useRouter();
  const { query } = useRouteParams();
  const [isRouteLoading, setIsRouteLoading] = useState(false);

  const handleRouteStart = useCallback(
    (newUrl: string, { shallow }: { shallow: boolean }) => {
      const newParams = new URLSearchParams(newUrl.split("?")[1]);
      const existingParams = new URLSearchParams(sanitiseQueryParams(query));
      const onlyChunkIsChanging = onlyChunkParamHasChanged(
        newParams,
        existingParams,
      );

      if (!shallow && !onlyChunkIsChanging) {
        setIsRouteLoading(true);
      }
    },
    [query],
  );

  const handleRouteComplete = useCallback(
    (_url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow) {
        setIsRouteLoading(false);
        window.dispatchEvent(new Event("jl.route.change"));
      }
    },
    [],
  );

  const handleRouteError = useCallback(
    (_err: Error, _url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow) {
        setIsRouteLoading(false);
        window.dispatchEvent(new Event("jl.route.change"));
      }
    },
    [],
  );

  useEffect(
    function subscribeToNextRouteEvents() {
      router.events.on("routeChangeStart", handleRouteStart);
      router.events.on("routeChangeComplete", handleRouteComplete);
      router.events.on("routeChangeError", handleRouteError);

      return function cleanupNextRouteEvents() {
        router.events.off("routeChangeStart", handleRouteStart);
        router.events.off("routeChangeComplete", handleRouteComplete);
        router.events.off("routeChangeError", handleRouteError);
      };
    },
    [router.events, handleRouteStart, handleRouteComplete, handleRouteError],
  );

  useEffect(function subscribeToPopStateEvents() {
    // browser back/forward button scroll to top fix
    const handlePopState = () => {
      window.dispatchEvent(new Event("jl.route.change"));
      const isAtPageBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 5;
      if (isAtPageBottom) {
        document.querySelector("#main-content")?.scrollIntoView({
          behavior: "instant",
        });
      }
    };

    window.addEventListener("popstate", handlePopState);

    return function cleanupPopStateHandler() {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return isRouteLoading;
}
